import axios from "axios";
import { encryptPayload, decryptPayload, isEncryptedEnvelope } from "@/lib/crypto";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

// ---------------------------------------------------------------------------
// Request interceptor: encrypt outgoing JSON bodies
// ---------------------------------------------------------------------------
api.interceptors.request.use(async (config) => {
  // Auth token
  const token = localStorage.getItem("payflex_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Portal domain header (for role-domain enforcement)
  config.headers = config.headers || {};
  config.headers["X-Portal-Domain"] = window.location.origin;

  // Encrypt request body for POST/PUT/PATCH with JSON data
  if (
    config.data &&
    typeof config.data === "object" &&
    !config._skipEncrypt &&
    ["post", "put", "patch"].includes((config.method || "").toLowerCase())
  ) {
    try {
      const envelope = await encryptPayload(config.data);
      config.data = envelope;
    } catch (err) {
      console.warn("Request encryption failed, sending plain:", err);
      // Fall through with unencrypted data
    }
  }

  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor: decrypt incoming encrypted responses
// ---------------------------------------------------------------------------
api.interceptors.response.use(
  async (response) => {
    if (isEncryptedEnvelope(response.data)) {
      try {
        response.data = await decryptPayload(response.data);
      } catch (err) {
        console.error("Response decryption failed:", err);
        throw err;
      }
    }
    return response;
  },
  async (error) => {
    // Also try to decrypt error responses
    if (
      error.response &&
      error.response.data &&
      isEncryptedEnvelope(error.response.data)
    ) {
      try {
        error.response.data = await decryptPayload(error.response.data);
      } catch {
        // If decryption fails on error response, leave as-is
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
